// loadUser is imported dynamically as it's app-specific
import type { UniqueEvents, User, UserAuth } from '@repo/common';
import type { DataPipeOptions, IDataPipe } from '@repo/common/types/DataPipe';
import { get } from 'svelte/store';
import { loadStoredUserAuth, user, userAuthStore } from '../stores/auth.store';
import { calendarsView, safeRefreshCalendars } from '../stores/provider.calendars.store';
import { waitForAuth } from '../stores/waitForAuth';

const DEFAULT_OPTIONS: DataPipeOptions = {
  cacheSource: 'auto',
  timeoutMs: 5000
};

import { createDataPipeLogger } from '../utils/logger.util';
// Import loadUser function from the app-specific auth.actions

const logger = createDataPipeLogger();

export class DataPipe implements IDataPipe {
  private promiseCache = new Map<string, Promise<any>>();
  private resultCache = new Map<string, any>();
  private dependencies: Record<string, string[]> = {};
  private resolvers: Record<
    string,
    (options: DataPipeOptions, overrides: Record<string, any>) => Promise<any>
  > = {};

  constructor() {
    this.setupDependencies();

    return new Proxy(this, {
      get(target, prop, receiver) {
        if (typeof prop === 'string' && !Reflect.has(target, prop) && !prop.startsWith('_')) {
          return (
            options: DataPipeOptions = { cacheSource: 'auto' },
            overrides: Record<string, any> = {}
          ) => {
            return target.executeWithDependencies(prop, options, overrides);
          };
        }
        return Reflect.get(target, prop, receiver);
      }
    });
  }

  private setupDependencies(): void {
    this.dependencies = {
      auth: [],
      user: ['auth'],
      calendarProviders: ['auth', 'user'],
      calendarEvents: ['auth', 'user', 'calendarProviders'],
      calendarData: ['auth', 'user'],
      transcripts: ['auth', 'user'],
      threads: ['auth', 'user'],
      driveProvider: ['auth', 'user', 'calendarProviders'],
      driveAccessToken: ['auth', 'user', 'driveProvider'],
      exportToGoogleDocs: ['auth', 'user', 'driveProvider']
    };

    this.resolvers = {
      auth: this.resolveAuth.bind(this),
      user: this.resolveUser.bind(this),
      calendarProviders: this.resolveCalendarProviders.bind(this),
      calendarEvents: this.resolveCalendarEvents.bind(this),
      calendarData: this.resolveCalendarData.bind(this),
      transcripts: this.resolveTranscripts.bind(this),
      threads: this.resolveThreads.bind(this),
      driveProvider: this.resolveDriveProvider.bind(this),
      driveAccessToken: this.resolveDriveAccessToken.bind(this),
      exportToGoogleDocs: this.resolveExportToGoogleDocs.bind(this)
    };
  }

  private getCacheKey(name: string, options: DataPipeOptions): string {
    return `${name}:${JSON.stringify(options)}`;
  }

  private executeWithDependencies(
    name: string,
    options: DataPipeOptions = { cacheSource: 'auto' },
    overrides: Record<string, any> = {}
  ): Promise<any> {
    if (!this.resolvers[name]) {
      return Promise.reject(new Error(`No resolver found for data pipe: ${name}`));
    }

    const mergedOptions = { ...DEFAULT_OPTIONS, ...options };
    const cacheKey = this.getCacheKey(name, mergedOptions);

    if (this.resultCache.has(cacheKey)) {
      return Promise.resolve(this.resultCache.get(cacheKey));
    }

    if (this.promiseCache.has(cacheKey)) {
      return this.promiseCache.get(cacheKey)!;
    }

    const promise = this.executePipelineStep(name, mergedOptions, overrides)
      .then((result) => {
        this.resultCache.set(cacheKey, result);
        this.promiseCache.delete(cacheKey);
        return result;
      })
      .catch((error) => {
        this.promiseCache.delete(cacheKey);
        throw error;
      });

    this.promiseCache.set(cacheKey, promise);

    return promise;
  }

  private async executePipelineStep(
    name: string,
    options: DataPipeOptions,
    overrides: Record<string, any>
  ): Promise<any> {
    const deps = this.dependencies[name] || [];
    const resolver = this.resolvers[name];

    if (!resolver) {
      logger.error(`No resolver found for data pipe: ${name}`);
      throw new Error(`No resolver found for data pipe: ${name}`);
    }

    logger.debug(`Executing pipeline step: ${name}`, { dependencies: deps, options });

    const resolvedDependencies: Record<string, any> = {};

    for (const depName of deps) {
      if (overrides[depName] !== undefined) {
        logger.debug(`Using override for dependency: ${depName}`);
        resolvedDependencies[depName] = overrides[depName];
      } else {
        logger.debug(`Resolving dependency: ${depName}`);
        resolvedDependencies[depName] = await this.executeWithDependencies(
          depName,
          options,
          overrides
        );
      }
    }

    logger.debug(`Resolved all dependencies for: ${name}`, Object.keys(resolvedDependencies));

    try {
      const result = await resolver(options, { ...overrides, ...resolvedDependencies });
      logger.debug(`Successfully executed: ${name}`);
      return result;
    } catch (error) {
      logger.debug(`Failed to execute: ${name}`, error);
      throw error;
    }
  }

  private async resolveAuth(
    options: DataPipeOptions,
    _deps: Record<string, any>
  ): Promise<UserAuth> {
    try {
      logger.info('Resolving auth');

      // First check if auth tokens are already in the store
      const storeAuth = get(userAuthStore);
      console.log('[DataPipe.resolveAuth] Checking store auth:', {
        hasStoreAuth: !!storeAuth,
        hasIdToken: !!storeAuth?.idToken,
        hasRefreshToken: !!storeAuth?.refreshToken
      });
      if (storeAuth && storeAuth.idToken && storeAuth.refreshToken) {
        logger.info('Auth tokens found in store');
        return storeAuth;
      }

      // If not in store, try loading from storage (this works for desktop via keychain)
      const result = await loadStoredUserAuth(true);
      console.log('[DataPipe.resolveAuth] Result from loadStoredUserAuth:', {
        hasResult: !!result,
        hasIdToken: !!result?.idToken,
        hasRefreshToken: !!result?.refreshToken
      });
      if (result && result.idToken && result.refreshToken) {
        return result;
      } else {
        console.log('Auth not loaded from storage', result);
      }

      // For web (where loadStoredUserAuth returns empty because browser storage is noop),
      // we need to fetch user data which will set auth tokens in the process
      // Check if we're on web by seeing if the storage result was truly empty
      const isWeb = !result || (!result.idToken && !result.refreshToken);
      if (isWeb) {
        console.log(
          '[DataPipe.resolveAuth] Web platform detected, fetching user to get auth tokens'
        );
        try {
          try {
            // @ts-ignore - $lib/actions is only available in SvelteKit apps
            const { loadUser } = await import('$lib/actions/auth.actions');
            await loadUser();
          } catch (importError) {
            console.debug('loadUser not available in this context');
          }

          // Check if auth tokens are now in the store after loading user
          const authAfterLoad = get(userAuthStore);
          if (authAfterLoad && authAfterLoad.idToken && authAfterLoad.refreshToken) {
            logger.info('Auth tokens set after loading user');
            return authAfterLoad;
          }
        } catch (error) {
          console.warn('Failed to load user during auth resolution:', error);
        }
      }

      // Try waiting if it can't be loaded from storage
      await waitForAuth(options.timeoutMs);
      const auth = get(userAuthStore);

      if (!auth.idToken || !auth.refreshToken) {
        throw new Error('Auth tokens not available');
      }

      return auth;
    } catch (error) {
      throw new Error(`Auth resolution failed: ${error}`);
    }
  }

  private async resolveUser(_options: DataPipeOptions, _deps: Record<string, any>): Promise<User> {
    // const auth = deps.auth as UserAuth;

    try {
      // Import loadUser function from the app-specific auth.actions
      try {
        // @ts-ignore - $lib/actions is only available in SvelteKit apps
        const { loadUser } = await import('$lib/actions/auth.actions');
        await loadUser();
      } catch (importError) {
        console.debug('loadUser not available in this context');
      }
      const userData = get(user);

      if (!userData.id) {
        throw new Error('User data not available after loading');
      }

      return userData;
    } catch (error) {
      throw new Error(`User resolution failed: ${error}`);
    }
  }

  private async resolveCalendarProviders(
    options: DataPipeOptions,
    _deps: Record<string, any>
  ): Promise<any[]> {
    try {
      // const auth = deps.auth as UserAuth;
      // const user = deps.user as User;

      if (options.cacheSource === 'cloud' || options.cacheSource === 'auto') {
        await safeRefreshCalendars();
      }

      const calendarsData = get(calendarsView);
      return calendarsData.providers || [];
    } catch (error) {
      throw new Error(`Calendar providers resolution failed: ${error}`);
    }
  }

  private async resolveCalendarEvents(
    options: DataPipeOptions,
    _deps: Record<string, any>
  ): Promise<UniqueEvents[]> {
    const { refreshCalendarsEvents, calendarEvents } = await import('../stores/calendar.store');

    try {
      // const providers = deps.calendarProviders as any[];
      // const auth = deps.auth as UserAuth;
      // const user = deps.user as User;

      // Refresh calendar events if needed
      if (options.cacheSource === 'cloud' || options.cacheSource === 'auto') {
        await refreshCalendarsEvents();
      }

      // Get calendar events from the store
      const calendarData = get(calendarEvents);
      const allEvents: any[] = [];

      // Extract events from eventsByDate
      if (calendarData.eventsByDate) {
        for (const dateStr in calendarData.eventsByDate) {
          const eventsForDate = calendarData.eventsByDate[dateStr];
          if (eventsForDate) {
            for (const eventId in eventsForDate) {
              allEvents.push(eventsForDate[eventId]);
            }
          }
        }
      }

      return allEvents;
    } catch (error) {
      throw new Error(`Calendar events resolution failed: ${error}`);
    }
  }

  private async resolveCalendarData(
    options: DataPipeOptions,
    deps: Record<string, any>
  ): Promise<any[]> {
    const calendarEvents =
      deps.calendarEvents || (await this.executeWithDependencies('calendarEvents', options));
    return calendarEvents.providers || [];
  }

  private async resolveTranscripts(
    options: DataPipeOptions,
    _deps: Record<string, any>
  ): Promise<any[]> {
    const { transcripts } = await import('../stores/transcripts');
    try {
      if (options.cacheSource === 'cloud' || options.cacheSource === 'auto') {
        await transcripts.loadSummaries(options.cacheSource === 'cloud');
      }

      const $transcripts = get(transcripts);
      return Array.from($transcripts.idMap.values());
    } catch (error) {
      throw new Error(`Transcripts resolution failed: ${error}`);
    }
  }

  private async resolveThreads(
    options: DataPipeOptions,
    _deps: Record<string, any>
  ): Promise<any[]> {
    const { refreshThreads, threadsStore } = await import('../stores/threads.store');
    try {
      if (options.cacheSource === 'cloud' || options.cacheSource === 'auto') {
        await refreshThreads();
      }

      const $threads = get(threadsStore);
      return $threads.list;
    } catch (error) {
      throw new Error(`Threads resolution failed: ${error}`);
    }
  }

  private async resolveDriveProvider(
    _options: DataPipeOptions,
    deps: Record<string, any>
  ): Promise<any> {
    try {
      const providers = deps.calendarProviders as any[];
      const constants = (await import('../utils/constants')).default;

      logger.info('🔍 Resolving Drive provider', {
        providersCount: providers.length,
        requiredScopes: constants.GOOGLE_DRIVE_SCOPES
      });

      // Log all providers and their scopes
      providers.forEach((provider: any, index: number) => {
        logger.info(`Provider ${index}:`, {
          providerObject: provider,
          email: provider?.email,
          providerEmail: provider?.provider?.email,
          id: provider?.id,
          providerType: provider?.providerType,
          scopes: provider?.scopes,
          providerScopes: provider?.provider?.scopes,
          hasAllDriveScopes: constants.GOOGLE_DRIVE_SCOPES.some((scope: string) =>
            provider?.provider?.scopes?.includes(scope)
          ),
          missingScopes: constants.GOOGLE_DRIVE_SCOPES.filter(
            (scope: string) => !provider?.provider?.scopes?.includes(scope)
          )
        });
      });

      // Find a provider with ALL Google Drive scopes
      // Note: scopes are in provider.provider.scopes (nested structure from ProviderInfo)
      const driveProvider = providers.find((provider: any) =>
        constants.GOOGLE_DRIVE_SCOPES.every((scope: string) =>
          provider?.provider?.scopes?.includes(scope)
        )
      );

      if (!driveProvider) {
        logger.error('❌ No provider found with all required Drive scopes', {
          totalProviders: providers.length,
          requiredScopes: constants.GOOGLE_DRIVE_SCOPES
        });
        throw new Error(
          'No Google Drive provider found. Please connect Google Drive in Account Settings.'
        );
      }

      logger.info('✅ Google Drive provider found:', {
        email: driveProvider.email,
        id: driveProvider.id,
        scopes: driveProvider.scopes
      });
      return driveProvider;
    } catch (error) {
      throw new Error(`Drive provider resolution failed: ${error}`);
    }
  }

  private async resolveDriveAccessToken(
    _options: DataPipeOptions,
    deps: Record<string, any>
  ): Promise<string> {
    try {
      const auth = deps.auth as UserAuth;
      const driveProvider = deps.driveProvider;

      if (!driveProvider?.id) {
        throw new Error('No Drive provider ID available');
      }

      logger.info('Fetching Drive access token from backend', { providerId: driveProvider.id });

      // Call backend API to get the access token
      const { getPrivateClient } = await import('../data/common.data');
      const client = getPrivateClient(auth);

      const response = await client.post('/drive/token', {
        providerId: driveProvider.id
      });

      if (!response.data.success || !response.data.accessToken) {
        throw new Error(response.data.error || 'Failed to get access token');
      }

      logger.info('✅ Drive access token resolved');
      return response.data.accessToken;
    } catch (error) {
      throw new Error(`Drive access token resolution failed: ${error}`);
    }
  }

  private async resolveExportToGoogleDocs(
    _options: DataPipeOptions,
    deps: Record<string, any>
  ): Promise<(title: string, htmlContent: string, existingDocId?: string | null) => Promise<any>> {
    try {
      const auth = deps.auth as UserAuth;
      const driveProvider = deps.driveProvider;

      logger.info('Export to Google Docs ready', {
        hasAuth: !!auth,
        hasProvider: !!driveProvider,
        providerEmail: driveProvider?.email
      });

      // Return a function that can be called to export
      // Pass the resolved provider ID to ensure we use the correct provider
      const providerId = driveProvider?.id;
      return async (title: string, htmlContent: string, existingDocId?: string | null) => {
        const { exportToGoogleDocs } = await import('../data/drive.data');
        return await exportToGoogleDocs(auth, title, htmlContent, existingDocId, providerId);
      };
    } catch (error) {
      throw new Error(`Export to Google Docs resolution failed: ${error}`);
    }
  }

  public async auth(
    options: DataPipeOptions = { cacheSource: 'auto' },
    overrides: Record<string, any> = {}
  ): Promise<UserAuth> {
    return this.executeWithDependencies('auth', options, overrides);
  }

  public async user(
    options: DataPipeOptions = { cacheSource: 'auto' },
    overrides: Record<string, any> = {}
  ): Promise<User> {
    return this.executeWithDependencies('user', options, overrides);
  }

  public async calendarProviders(
    options: DataPipeOptions = { cacheSource: 'auto' },
    overrides: Record<string, any> = {}
  ): Promise<any[]> {
    return this.executeWithDependencies('calendarProviders', options, overrides);
  }

  public async calendarEvents(
    options: DataPipeOptions = { cacheSource: 'auto' },
    overrides: Record<string, any> = {}
  ): Promise<any[]> {
    return this.executeWithDependencies('calendarEvents', options, overrides);
  }

  public async calendarData(
    options: DataPipeOptions = { cacheSource: 'auto' },
    overrides: Record<string, any> = {}
  ): Promise<any[]> {
    return this.executeWithDependencies('calendarData', options, overrides);
  }

  public async transcripts(
    options: DataPipeOptions = { cacheSource: 'auto' },
    overrides: Record<string, any> = {}
  ): Promise<any[]> {
    return this.executeWithDependencies('transcripts', options, overrides);
  }

  public async threads(
    options: DataPipeOptions = { cacheSource: 'auto' },
    overrides: Record<string, any> = {}
  ): Promise<any[]> {
    return this.executeWithDependencies('threads', options, overrides);
  }

  public invalidateCache(name?: string): void {
    if (name) {
      const keysToDelete = Array.from(this.resultCache.keys()).filter((key) =>
        key.startsWith(name + ':')
      );
      logger.debug(`Invalidating cache for: ${name}`, { keysCount: keysToDelete.length });
      keysToDelete.forEach((key) => {
        this.resultCache.delete(key);
        this.promiseCache.delete(key);
      });
    } else {
      logger.debug('Clearing all caches', {
        resultCacheSize: this.resultCache.size,
        promiseCacheSize: this.promiseCache.size
      });
      this.resultCache.clear();
      this.promiseCache.clear();
    }
  }

  public getDependencyGraph(): Record<string, string[]> {
    return { ...this.dependencies };
  }

  public async driveProvider(
    options: DataPipeOptions = { cacheSource: 'auto' },
    overrides: Record<string, any> = {}
  ): Promise<any> {
    return this.executeWithDependencies('driveProvider', options, overrides);
  }

  public async driveAccessToken(
    options: DataPipeOptions = { cacheSource: 'auto' },
    overrides: Record<string, any> = {}
  ): Promise<string> {
    return this.executeWithDependencies('driveAccessToken', options, overrides);
  }

  public async exportToGoogleDocs(
    options: DataPipeOptions = { cacheSource: 'auto' },
    overrides: Record<string, any> = {}
  ): Promise<(title: string, htmlContent: string, existingDocId?: string | null) => Promise<any>> {
    return this.executeWithDependencies('exportToGoogleDocs', options, overrides);
  }

  public addPipeMethod<T>(
    name: string,
    dependencies: string[],
    resolver: (options: DataPipeOptions, deps: Record<string, any>) => Promise<T>
  ): void {
    logger.debug(`Adding new pipe method: ${name}`, { dependencies });
    this.dependencies[name] = dependencies;
    this.resolvers[name] = resolver;

    (this as any)[name] = (
      options: DataPipeOptions = { cacheSource: 'auto' },
      overrides: Record<string, any> = {}
    ): Promise<T> => {
      return this.executeWithDependencies(name, options, overrides);
    };
  }
}
